import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import ReportsLibrary from './components/ReportsLibrary';
import SchedulingPanel from './components/SchedulingPanel';
import ValidationChecks from './components/ValidationChecks';
import QuickActionToolbar from './components/QuickActionToolbar';

const ReportsManagementDashboard = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');

  const reportCategories = [
    { id: 'all', label: 'All Reports', icon: 'FileText', count: 24 },
    { id: 'compliance', label: 'Compliance Reports', icon: 'Shield', count: 8 },
    { id: 'safety-analytics', label: 'Safety Analytics', icon: 'BarChart3', count: 6 },
    { id: 'operational', label: 'Operational Reports', icon: 'Activity', count: 5 },
    { id: 'custom', label: 'Custom Reports', icon: 'FileEdit', count: 5 },
  ];

  const reports = [
    {
      id: 1,
      name: 'Statutory Compliance Status',
      category: 'compliance',
      lastGenerated: '12/05/2025',
      frequency: 'Monthly',
      status: 'ready',
      validation: 'complete',
      location: 'all',
      description: 'Comprehensive status of all statutory compliance requirements',
    },
    {
      id: 2,
      name: 'Audit Summary Report',
      category: 'compliance',
      lastGenerated: '12/04/2025',
      frequency: 'Weekly',
      status: 'ready',
      validation: 'complete',
      location: 'plant-1',
      description: 'Summary of completed audits and findings',
    },
    {
      id: 3,
      name: 'Training Completion Report',
      category: 'compliance',
      lastGenerated: '12/03/2025',
      frequency: 'Monthly',
      status: 'ready',
      validation: 'complete',
      location: 'all',
      description: 'Personnel training completion and certification status',
    },
    {
      id: 4,
      name: 'Incident Trends Analysis',
      category: 'safety-analytics',
      lastGenerated: '12/06/2025',
      frequency: 'Monthly',
      status: 'ready',
      validation: 'complete',
      location: 'all',
      description: 'Analysis of incident patterns and trends',
    },
    {
      id: 5,
      name: 'Unsafe Acts Analysis',
      category: 'safety-analytics',
      lastGenerated: '12/05/2025',
      frequency: 'Weekly',
      status: 'ready',
      validation: 'complete',
      location: 'all',
      description: 'Analysis of unsafe acts and conditions observations',
    },
    {
      id: 6,
      name: 'Risk Assessment Report',
      category: 'safety-analytics',
      lastGenerated: '12/02/2025',
      frequency: 'Quarterly',
      status: 'ready',
      validation: 'pending',
      location: 'plant-2',
      description: 'Comprehensive risk assessment and mitigation strategies',
    },
    {
      id: 7,
      name: 'Work Permit Analytics',
      category: 'operational',
      lastGenerated: '12/06/2025',
      frequency: 'Weekly',
      status: 'ready',
      validation: 'complete',
      location: 'all',
      description: 'Work permit issuance and compliance statistics',
    },
    {
      id: 8,
      name: 'Equipment Safety Status',
      category: 'operational',
      lastGenerated: '12/04/2025',
      frequency: 'Monthly',
      status: 'ready',
      validation: 'complete',
      location: 'plant-1',
      description: 'Safety inspection and maintenance status of equipment',
    },
  ];

  const upcomingReports = [
    {
      id: 1,
      name: 'Monthly Safety Review',
      scheduledDate: '12/15/2025',
      type: 'Automated',
      validation: 'complete',
      recipients: 5,
    },
    {
      id: 2,
      name: 'Compliance Dashboard',
      scheduledDate: '12/10/2025',
      type: 'Automated',
      validation: 'pending',
      recipients: 3,
    },
    {
      id: 3,
      name: 'Training Analytics',
      scheduledDate: '12/20/2025',
      type: 'Scheduled',
      validation: 'complete',
      recipients: 8,
    },
  ];

  const validationMetrics = {
    dataCompleteness: 94,
    regulatoryCompliance: 98,
    lastUpdated: '2 hours ago',
    issues: 3,
  };

  const handleGenerateReport = (report) => {
    console.log('Generating report:', report?.name);
  };

  const handleScheduleReport = (report) => {
    console.log('Scheduling report:', report?.name);
  };

  const handleCreateCustomReport = () => {
    navigate('/custom-report-builder');
  };

  const handleGenerateNow = (report) => {
    console.log('Generate now:', report?.name);
  };

  const handleExport = (report) => {
    console.log('Export report:', report?.name);
  };

  const filteredReports = reports?.filter((report) => {
    const matchesCategory = selectedCategory === 'all' || report?.category === selectedCategory;
    const matchesSearch = report?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         report?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || report?.location === selectedLocation || report?.location === 'all';
    return matchesCategory && matchesSearch && matchesLocation;
  });

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-foreground">Reports Management Dashboard</h1>
          <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">All Systems Active</span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Comprehensive report generation and management system with automated scheduling and validation
        </p>
      </div>
      <QuickActionToolbar
        onGenerateReport={handleCreateCustomReport}
        onScheduleReport={handleCreateCustomReport}
        onCreateCustomReport={handleCreateCustomReport}
      />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        {reportCategories?.map((category) => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`p-4 rounded-lg border transition-all ${
              selectedCategory === category?.id
                ? 'bg-primary border-primary text-primary-foreground'
                : 'bg-card border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <Icon 
                name={category?.icon} 
                size={24} 
                color={selectedCategory === category?.id ? '#ffffff' : 'var(--color-primary)'} 
              />
              <span className="text-2xl font-bold">{category?.count}</span>
            </div>
            <p className={`text-sm font-medium ${
              selectedCategory === category?.id ? 'text-primary-foreground' : 'text-foreground'
            }`}>
              {category?.label}
            </p>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-foreground">Reports Library</h2>
              <div className="flex items-center gap-4">
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e?.target?.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="all">All Locations</option>
                  <option value="plant-1">Plant 1</option>
                  <option value="plant-2">Plant 2</option>
                  <option value="plant-3">Plant 3</option>
                  <option value="warehouse-1">Warehouse 1</option>
                </select>
                <div className="relative">
                  <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search reports..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground"
                  />
                </div>
              </div>
            </div>
            <ReportsLibrary
              reports={filteredReports}
              onGenerate={handleGenerateReport}
              onSchedule={handleScheduleReport}
              onExport={handleExport}
            />
          </div>
        </div>

        <div className="space-y-6">
          <SchedulingPanel
            upcomingReports={upcomingReports}
            onGenerateNow={handleGenerateNow}
          />
          <ValidationChecks metrics={validationMetrics} />
        </div>
      </div>
    </div>
  );
};

export default ReportsManagementDashboard;